import { useState, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@altimateai/lego";
import { bulkDeleteMemories } from "@/helpers/apis";
import { useAppState } from "@/modules/app/useAppContext";
import { Memory } from "./types";
import { Teammate } from "@/pages/mcp/mcpSlice";

interface UseBulkDeleteMemoriesProps {
  memories: Memory[];
  datamatesById?: Record<string, Teammate>;
}

const useBulkDeleteMemories = ({ memories, datamatesById }: UseBulkDeleteMemoriesProps) => {
  const [selectedMemoryIds, setSelectedMemoryIds] = useState<string[]>([]);
  const { loggedinUser: currentUser } = useAppState();
  const queryClient = useQueryClient();

  const bulkDeleteMutation = useMutation({
    mutationFn: bulkDeleteMemories,
    onSuccess: () => {
      toast({ title: "Memories deleted successfully", variant: "default" });
      queryClient.invalidateQueries({ queryKey: ["memories"] });
      setSelectedMemoryIds([]);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete memories",
        variant: "destructive",
        description: error.message,
      });
    },
  });

  const ownedMemories = useMemo(
    () =>
      memories.filter(memory => {
        const datamate = datamatesById?.[memory.metadata?.datamate_id || ""];
        return datamate && currentUser && datamate.user_id === currentUser.id;
      }),
    [memories, datamatesById, currentUser]
  );

  const selectableMemoryCount = ownedMemories.length;
  const isAllSelected =
    selectedMemoryIds.length === selectableMemoryCount && selectableMemoryCount > 0;
  const isIndeterminate =
    selectedMemoryIds.length > 0 && selectedMemoryIds.length < selectableMemoryCount;

  const handleSelectAll = (checked: boolean | "indeterminate") => {
    if (checked === true) {
      const ownedMemoryIds = ownedMemories.map(memory => memory.id);
      setSelectedMemoryIds(ownedMemoryIds);
    } else {
      setSelectedMemoryIds([]);
    }
  };

  const handleSelectMemory = (memoryId: string, checked: boolean) => {
    if (checked) {
      setSelectedMemoryIds(prev => [...prev, memoryId]);
    } else {
      setSelectedMemoryIds(prev => prev.filter(id => id !== memoryId));
    }
  };

  const handleBulkDelete = () => {
    if (selectedMemoryIds.length > 0) {
      bulkDeleteMutation.mutate(selectedMemoryIds);
    }
  };

  const canUserDeleteMemory = (memory: Memory): boolean => {
    return memory.user_id === currentUser?.uuid;
  };

  return {
    selectedMemoryIds,
    setSelectedMemoryIds,
    isAllSelected,
    isIndeterminate,
    handleSelectAll,
    handleSelectMemory,
    handleBulkDelete,
    bulkDeleteMutation,
    canUserDeleteMemory,
    selectableMemoryCount,
  };
};

export default useBulkDeleteMemories;
